import { Button, Card, Col, Row, Space } from 'antd';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useTranslation } from 'react-i18next';
import { AutoCompleteKeyMap } from '../../../../components/GrispiInput/components/GrispiAutoCompleteInput';
import GrispiDateRangeInput, {
    PresetDateNameMap
} from '../../../../components/GrispiInput/components/GrispiDateRangeInput';
import GrispiInput from '../../../../components/GrispiInput/index';
import useCompanyTimeZone from '../../../../hooks/adminPage/useCompanyTimeZone';
import useChannelsAsOptions from '../../../../hooks/useChannelsAsOptions';
import useUserById, { CustomizedDetailedUser, deriveCustomizedDetailedUser } from '../../../../hooks/useUserById';
import { reportTypeMap } from '../../../../layouts/Pages/ReportsLayout';
import {
    parseSerializedValueFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../../../utils/forms/formHelper.parser';
import { guessTimeZone, nonNullishValue, nullishValue } from '../../../../utils/helpers';

export enum TicketFiltersQueryKeyMap {
    'startDate' = 'startDate',
    'endDate' = 'endDate',
    'agent' = 'agentId',
    'group' = 'groupId',
    'form' = 'formId',
    'channel' = 'channel',
    'zone' = 'zone'
}
export enum TicketFiltersMandatoryQueryKeys {
    'startDate' = 'startDate',
    'endDate' = 'endDate',
    'zone' = 'zone'
}

enum TicketReportFiltersTypeMap {
    CHANNEL = 'CHANNEL',
    GROUP = 'GROUP',
    FORM = 'FORM',
    AGENT = 'AGENT',
    DATE = 'DATE'
}

/**
 * It generates a query string from a query object
 * @param {ParsedUrlQuery} query - ParsedUrlQuery
 * @returns A string
 */
export const generateReportQueryStringFromQuery = (
    query: ParsedUrlQuery,
    config?:
        | {
              paramKey: TicketFiltersQueryKeyMap;
              paramValue: string | number;
              resetQuery?: never;
              zone?: never;
          }
        | {
              paramKey: 'DATE';
              paramValue: string;
              zone: string;
              resetQuery?: never;
          }
        | {
              paramKey?: never;
              paramValue?: never;
              resetQuery: boolean;
              zone?: never;
          }
) => {
    let generatedQueryString = Object.keys(query)
        .filter((param) =>
            config?.paramKey === TicketReportFiltersTypeMap.DATE
                ? param !== TicketFiltersQueryKeyMap.startDate &&
                  param !== TicketFiltersQueryKeyMap.endDate &&
                  param !== TicketFiltersQueryKeyMap.zone &&
                  param !== 'reportType'
                : param !== 'reportType'
        )
        .map((param) => {
            return `${param}=${
                !config?.resetQuery ? (param !== config?.paramKey ? query[param].toString() : config?.paramValue) : null
            }`;
        });
    if (config?.paramKey && !query[config?.paramKey] && config?.paramKey !== TicketReportFiltersTypeMap.DATE)
        generatedQueryString.push(config?.paramKey + '=' + config?.paramValue);
    if (config?.paramKey === TicketReportFiltersTypeMap.DATE && config?.paramValue) {
        switch (query?.reportType) {
            case reportTypeMap.agent_status:
                generatedQueryString.push(TicketFiltersQueryKeyMap.startDate + '=' + config?.paramValue?.split('-')[0]);
                generatedQueryString.push(TicketFiltersQueryKeyMap.endDate + '=' + config?.paramValue?.split('-')[1]);
                break;
            case reportTypeMap.agent_status_snapshot:
                break;
            default:
                generatedQueryString.push(TicketFiltersQueryKeyMap.startDate + '=' + config?.paramValue?.split('-')[0]);
                generatedQueryString.push(TicketFiltersQueryKeyMap.endDate + '=' + config?.paramValue?.split('-')[1]);
                generatedQueryString.push(
                    TicketFiltersQueryKeyMap.zone +
                        '=' +
                        (nonNullishValue(config?.zone) ? config?.zone : guessTimeZone())
                );
                break;
        }

        /*
         * TODO: Temporarily it's OK to get Turkey zone as default.
         *  However, we shouldn't let users to see reports as per Turkey time-zone if no zone available.
         *  This can make user to see wrong reports prepared with wrong time-zone.
         *  After we implement deprecation of ssr,
         *  We can show modal to inform user that we will redirect them to XXX time-zone in accordance with
         *  their other possible language options company, user or browser language, respectively.
         */
    }

    if (!config) {
        generatedQueryString = Object.keys(query)
            .filter((param) => param !== 'reportType')
            .map((param) => {
                return `${param}=${query[param].toString()}`;
            });
        return generatedQueryString.join('&');
    }

    return generatedQueryString.filter((queryString) => !queryString.includes('null')).join('&');
};

function deriveAgentFormValue(agentData: CustomizedDetailedUser): string {
    const serializedValue = agentData?.id?.toString();
    if (nullishValue(serializedValue)) return null;

    const userFriendlyValueEmail = agentData?.primaryEmail?.email;
    let userFriendlyValueFullName = agentData?.fullName;

    if (!userFriendlyValueFullName) {
        if (!userFriendlyValueEmail) return serializedValue;

        userFriendlyValueFullName = userFriendlyValueEmail;
        return `${agentData.id}|${userFriendlyValueFullName}`;
    }

    if (!userFriendlyValueEmail) return `${agentData.id}|${userFriendlyValueFullName}`;

    return `${agentData.id}|${userFriendlyValueFullName} (${userFriendlyValueEmail})`;
}

const TicketReportFiltersContainer = () => {
    const {
        t,
        i18n: { language }
    } = useTranslation('reports');

    const { query, push } = useRouter();
    const { agentId, startDate, endDate, channel, groupId, formId } = query;
    const initialAgent = agentId !== undefined ? agentId?.toString() : null;
    const initialDate =
        startDate !== undefined && endDate !== undefined ? startDate?.toString() + '-' + endDate?.toString() : null;
    const initialChannel = channel !== undefined ? channel?.toString() : null;
    const initialGroup = groupId !== undefined ? parseInt(groupId?.toString()) : null;
    const initialForm = formId !== undefined ? parseInt(formId?.toString()) : null;

    const { data } = useUserById(initialAgent);
    const agentData = deriveCustomizedDetailedUser(data);

    const agentValue = deriveAgentFormValue(agentData);

    const { data: channelOptions } = useChannelsAsOptions();
    const { data: zone } = useCompanyTimeZone();

    const handleChange = (value: string | number, filterType: keyof typeof TicketReportFiltersTypeMap | 'RESET') => {
        let queryString = '';
        switch (filterType) {
            case TicketReportFiltersTypeMap.AGENT:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: TicketFiltersQueryKeyMap[filterType.toLowerCase()],
                    paramValue: parseUserIdFromSerializedValue(parseSerializedValueFromUserFormValue(value?.toString()))
                });
                break;
            case TicketReportFiltersTypeMap.DATE:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: TicketReportFiltersTypeMap.DATE,
                    paramValue: value?.toString(),
                    zone
                });
                break;
            case 'RESET':
                queryString = generateReportQueryStringFromQuery(query, {
                    resetQuery: true
                });
                break;
            default:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: TicketFiltersQueryKeyMap[filterType.toLowerCase()],
                    paramValue: value
                });
                break;
        }
        push(`/reports/tickets?${queryString}`);
    };
    return (
        <Space direction="vertical" className="full-width" size="small">
            <Card
                className="full-width"
                bodyStyle={{ padding: '12px' }}
                style={{ borderRadius: '5px', border: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
                <Row gutter={[10, 5]} justify="space-between" className="full-width">
                    <Col span={24}>
                        <Row gutter={[10, 5]}>
                            <Col span={6}>
                                <GrispiDateRangeInput
                                    rangePickerProps={{ className: 'report-date-filter-input' }}
                                    onChange={(value) => handleChange(value, TicketReportFiltersTypeMap.DATE)}
                                    value={initialDate}
                                    containerProps={{ label: t('ticketReports.dateSelection.label') }}
                                    presetDateRange={PresetDateNameMap.thisMonth}
                                />
                            </Col>
                            <Col span={6}>
                                <GrispiInput
                                    containerProps={{ label: t('ticketReports.agent.label') }}
                                    type="USER"
                                    antdProps={{ placeholder: t('ticketReports.agent.placeholder') }}
                                    isAgentInput={true}
                                    value={agentValue}
                                    onChange={(value) => handleChange(value, TicketReportFiltersTypeMap.AGENT)}
                                    conditionInput
                                />
                            </Col>

                            <Col span={4}>
                                <GrispiInput
                                    containerProps={{ label: t('ticketReports.group.label') }}
                                    type="AUTOCOMPLETE"
                                    autocompleteKey={AutoCompleteKeyMap.group}
                                    antdProps={{ placeholder: t('ticketReports.group.placeholder') }}
                                    value={initialGroup}
                                    onChange={(value) => handleChange(value, TicketReportFiltersTypeMap.GROUP)}
                                />
                            </Col>

                            <Col span={4}>
                                <GrispiInput
                                    containerProps={{ label: t('ticketReports.form.label') }}
                                    type="FORM"
                                    antdProps={{ placeholder: t('ticketReports.form.placeholder') }}
                                    value={initialForm}
                                    onChange={(value) => handleChange(value, TicketReportFiltersTypeMap.FORM)}
                                />
                            </Col>

                            <Col span={4}>
                                {!!channelOptions?.length && (
                                    <GrispiInput
                                        key={'Channel' + language}
                                        containerProps={{ label: t('ticketReports.channel.label') }}
                                        type="SELECT"
                                        options={channelOptions}
                                        antdProps={{ placeholder: t('ticketReports.channel.placeholder') }}
                                        value={initialChannel}
                                        onChange={(value) => handleChange(value, TicketReportFiltersTypeMap.CHANNEL)}
                                    />
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Space
                                className="full-width"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    alignItems: 'end',
                                    marginTop: '10px'
                                }}
                            >
                                <Button
                                    type="primary"
                                    onClick={() => handleChange(null, 'RESET')}
                                    disabled={
                                        !initialAgent &&
                                        !initialChannel &&
                                        !initialForm &&
                                        !initialDate &&
                                        !initialGroup
                                    }
                                >
                                    {t('ticketReports.clearButtonLabel')}
                                </Button>
                            </Space>
                        </Row>
                    </Col>
                </Row>
            </Card>
        </Space>
    );
};

export default TicketReportFiltersContainer;
